// src/components/layout/Navbar.jsx
import { useState } from "react";
import { useNavigate, useLocation, useParams, Link } from "react-router-dom";
import { Menu, X, LogOut, User, ChevronDown, LayoutDashboard } from "lucide-react";
import BookyaraLogo from "../ui/BookyaraLogo";
import { useAuth } from "../../context/AuthContext";
import { useTenant } from "../../context/TenantContext";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const { tenant }               = useTenant();
  const navigate   = useNavigate();
  const { pathname } = useLocation();
  const { slug }   = useParams();

  const base = `/${slug}`;
  const NAV_LINKS = [
    { label: "Home",     path: base },
    { label: "Services", path: `${base}/services` },
    { label: "About",    path: `${base}/about` },
  ];

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-surface-200 shadow-sm">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <button onClick={() => navigate(base)} className="flex items-center gap-2 group hover:opacity-80 transition-opacity">
            {tenant?.logoUrl ? (
              <>
                <img src={tenant.logoUrl} alt={tenant.name} className="w-8 h-8 object-cover rounded-lg shrink-0" />
                <span className="font-semibold text-xl text-ink-900 tracking-tight">{tenant.name}</span>
              </>
            ) : (
              <BookyaraLogo size="sm" />
            )}
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map(link => (
              <button key={link.path} onClick={() => navigate(link.path)}
                className={`nav-link ${pathname === link.path ? "nav-link-active" : ""}`}>
                {link.label}
              </button>
            ))}
          </nav>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <button onClick={() => navigate(`${base}/book`)} className="btn-primary text-sm px-5 py-2.5">
                  Book Now
                </button>
                <div className="relative">
                  <button onClick={() => setDropOpen(o => !o)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl border border-surface-200
                               hover:border-brand-300 hover:bg-brand-50 transition-all text-sm text-ink-600">
                    <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center
                                    text-brand-700 text-xs font-bold">
                      {user.displayName?.[0]?.toUpperCase() || "U"}
                    </div>
                    <span className="font-medium">{user.displayName?.split(" ")[0]}</span>
                    <ChevronDown size={14} className={`transition-transform ${dropOpen ? "rotate-180" : ""}`} />
                  </button>
                  {dropOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl border border-surface-200
                                    shadow-card-hover overflow-hidden animate-fade-in">
                      <button onClick={() => { navigate(`${base}/profile`); setDropOpen(false); }}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-ink-600
                                   hover:bg-brand-50 hover:text-brand-700 transition-colors">
                        <User size={15} /> My Bookings
                      </button>
                      {isAdmin && (
                        <>
                          <div className="divider" />
                          <button onClick={() => { navigate(`${base}/admin`); setDropOpen(false); }}
                            className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-brand-700
                                       hover:bg-brand-50 transition-colors font-semibold">
                            <LayoutDashboard size={15} /> Admin Dashboard
                          </button>
                        </>
                      )}
                      <div className="divider" />
                      <button onClick={handleLogout}
                        className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-red-500
                                   hover:bg-red-50 transition-colors">
                        <LogOut size={15} /> Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to={`/login?redirect=${base}`}  className="btn-ghost text-sm">Sign In</Link>
                <Link to={`/signup?tenant=${slug}&redirect=${base}`} className="btn-primary text-sm px-5 py-2.5">Get Started</Link>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <button onClick={() => setMenuOpen(o => !o)}
            className="md:hidden text-ink-500 hover:text-brand-600 transition-colors">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-surface-200 bg-white animate-fade-in">
          <div className="px-4 py-3 space-y-1">
            {NAV_LINKS.map(link => (
              <button key={link.path} onClick={() => { navigate(link.path); setMenuOpen(false); }}
                className={`w-full text-left px-4 py-2.5 rounded-xl text-sm transition-all
                  ${pathname === link.path ? "text-brand-600 bg-brand-50" : "text-ink-500 hover:bg-surface-100"}`}>
                {link.label}
              </button>
            ))}
            <div className="pt-2 space-y-2">
              {user ? (
                <>
                  <button onClick={() => { navigate(`${base}/book`); setMenuOpen(false); }}
                    className="btn-primary w-full text-sm">Book Now</button>
                  {isAdmin && (
                    <button onClick={() => { navigate(`${base}/admin`); setMenuOpen(false); }}
                      className="btn-secondary w-full text-sm">Admin Dashboard</button>
                  )}
                  <button onClick={handleLogout}
                    className="btn-secondary w-full text-sm text-red-500">Sign Out</button>
                </>
              ) : (
                <>
                  <Link to={`/login?redirect=${base}`} onClick={() => setMenuOpen(false)} className="btn-secondary w-full text-sm block text-center">Sign In</Link>
                  <Link to={`/signup?tenant=${slug}&redirect=${base}`} onClick={() => setMenuOpen(false)} className="btn-primary w-full text-sm block text-center">Get Started</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
